import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged, forkJoin, Subject, takeUntil } from "rxjs";
import { LogtoUser } from "../model/LogtoUser";
import { ManageUsersDialogData } from "./manage-users-dialog-data";
import { LogtoUsersService } from "../services/logto-users.service";
import { RolesService } from "../services/roles.service";
import { RoleAssignment, UserRole } from "../model/RoleAssignment";

interface AdminRow {
  assignment: RoleAssignment;
  user: LogtoUser | undefined;
}

@Component({
  selector: "app-manage-users-dialog",
  templateUrl: "./manage-users-dialog.component.html",
  styleUrls: ["./manage-users-dialog.component.css"],
})
export class ManageUsersDialogComponent implements OnInit, OnDestroy {
  searchControl = new FormControl("");
  users: LogtoUser[] = [];
  selectedUser: LogtoUser | null = null;
  admins: AdminRow[] = [];
  loading = false;
  saving = false;

  private allUsers: LogtoUser[] = [];
  private readonly role: UserRole;
  private readonly entityId: number;
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<ManageUsersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ManageUsersDialogData,
    private logtoUsersService: LogtoUsersService,
    private rolesService: RolesService,
  ) {
    this.role = data.entityType === "organization" ? "ORG_ADMIN" : "FACILITY_ADMIN";
    this.entityId = Number(data.entityId);
  }

  ngOnInit() {
    this.loading = true;
    const assignments$ =
      this.data.entityType === "organization"
        ? this.rolesService.listForOrganization(this.entityId)
        : this.rolesService.listForFacility(this.entityId);

    forkJoin({
      users: this.logtoUsersService.getUsers(),
      assignments: assignments$,
    }).subscribe({
      next: ({ users, assignments }) => {
        this.allUsers = users;
        this.buildAdmins(assignments);
        this.filterUsers("");
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => this.filterUsers(value ?? ""));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildAdmins(assignments: RoleAssignment[]) {
    this.admins = assignments.map((a) => ({
      assignment: a,
      user: this.allUsers.find((u) => u.id === a.logtoUserId),
    }));
  }

  private filterUsers(query: string) {
    const adminIds = new Set(this.admins.map((a) => a.assignment.logtoUserId));
    const available = this.allUsers.filter((u) => !adminIds.has(u.id));
    const q = query.trim().toLowerCase();
    this.users = !q
      ? available
      : available.filter(
          (u) =>
            u.primaryEmail?.toLowerCase().includes(q) ||
            u.name?.toLowerCase().includes(q) ||
            u.username?.toLowerCase().includes(q),
        );
  }

  onSelect(user: LogtoUser) {
    this.selectedUser = user;
  }

  addAdmin() {
    if (!this.selectedUser) {
      return;
    }
    const user = this.selectedUser;
    this.saving = true;
    this.rolesService
      .assign({
        logtoUserId: user.id,
        role: this.role,
        organizationId: this.data.entityType === "organization" ? this.entityId : null,
        facilityId: this.data.entityType === "facility" ? this.entityId : null,
      })
      .subscribe({
        next: (created) => {
          this.admins = [...this.admins, { assignment: created, user }];
          this.selectedUser = null;
          this.searchControl.setValue("");
          this.filterUsers("");
          this.saving = false;
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  removeAdmin(row: AdminRow) {
    this.rolesService.remove(row.assignment.id).subscribe({
      next: () => {
        this.admins = this.admins.filter((a) => a.assignment.id !== row.assignment.id);
        this.filterUsers(this.searchControl.value ?? "");
      },
    });
  }

  displayFn(user: LogtoUser | null): string {
    return user?.name || user?.primaryEmail || user?.username || "";
  }

  getInitials(user: LogtoUser | undefined): string {
    if (!user) {
      return "?";
    }
    if (user.name) {
      return user.name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    }
    return (user.primaryEmail?.[0] || user.username?.[0] || "?").toUpperCase();
  }

  close() {
    this.dialogRef.close();
  }
}
